import { Button, Dialog, DialogActions, DialogContent, Grid, List, ListItem, ListItemButton, ListItemText, Typography, useTheme } from "@mui/material";
import moment from "moment";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import StyledOutlinedTextField from "../../styledComponents/OutlinedTextField";

const twentyfourhours = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0]
const minutes = [0, 15, 30, 45, 0, 15, 30, 45, 0, 15, 30, 45, 0]

const MobileWheelTimePicker = ({ label, time, setTime, error, haveMargin, centerText, disable, setUpdated }) => {

    const theme = useTheme();

    const [isOpen, setIsOpen] = useState(false);
    const [initial, setInitial] = useState(moment());
    const [value, setValue] = useState(moment());
    const [hourIndex, setHourIndex] = useState(24);
    const [minuteIndex, setMinuteIndex] = useState(4);
    const [safariFix, setSafariFix] = useState(true);

    const hourRef = useRef([]);
    const minuteRef = useRef([]);

    const { t } = useTranslation();

    useEffect(() => {
        const newTime = moment(time);
        const indexForHours = twentyfourhours.findIndex(v => v === newTime.hours()) + 24;
        const indexForMinutes = minutes.findIndex(v => v === newTime.minutes()) + 4;
        setHourIndex(indexForHours);
        setMinuteIndex(indexForMinutes);
        if (time) {
            setInitial(time);
            setValue(time);
        }
    }, [time])

    const handleHourScroll = (event) => {
        if (event.target.scrollTop === 0) {
            hourRef.current[24]?.scrollIntoView({ block: "start" });
        }
        if (Math.trunc(event.target.scrollHeight - event.target.scrollTop) === event.target.clientHeight) {
            hourRef.current[24]?.scrollIntoView({ block: "end" });
        }
    }

    const handleMinuteScroll = (event) => {
        if (event.target.scrollTop === 0) {
            minuteRef.current[4]?.scrollIntoView({ block: "start" });
        }
        if (Math.trunc(event.target.scrollHeight - event.target.scrollTop) === event.target.clientHeight) {
            minuteRef.current[4]?.scrollIntoView({ block: "end" });
        }
    };

    const setHour = (hour) => {
        const newValue = moment(value);
        let index = twentyfourhours.findIndex(v => v === hour);
        if (index <= 1) {
            index = index + 24;
        }
        newValue.set("hour", hour);
        setValue(newValue);
        if (setUpdated) {
            setUpdated(true);
        }
        hourRef.current[index]?.scrollIntoView({
            block: "center",
            // behavior: "smooth"
        });
    }

    const setMinute = (minute) => {
        const newValue = moment(value);
        let index = minutes.findIndex(v => v === minute);
        if (index <= 1) {
            index = index + 4;
        }
        newValue.set("minute", minute);
        setValue(newValue);
        if (setUpdated) {
            setUpdated(true);
        }
        minuteRef.current[index]?.scrollIntoView({
            block: "center",
            // behavior: "smooth"
        });
    }

    return (
        <Fragment>
            <StyledOutlinedTextField
                label={label}
                value={moment(initial).format("HH:mm")}
                onClick={() => { setIsOpen(true); setSafariFix(true); }}
                inputProps={{ readOnly: true }}
                error={error}
                sx={{ ml: haveMargin ? 1.5 : 0, input: { textAlign: centerText ? "center" : "start" } }}
                disabled={disable}
            />
            <Dialog
                open={isOpen}
                onFocus={() => {
                    if (safariFix) {
                        hourRef.current[hourIndex]?.scrollIntoView({
                            block: "center",
                            // behavior: "smooth"
                        });
                        minuteRef.current[minuteIndex]?.scrollIntoView({
                            block: "center",
                            // behavior: "smooth"
                        });
                        setSafariFix(false);
                    }
                }}
                fullWidth
                maxWidth={"xs"}
                onClose={() => { setIsOpen(false); setValue(initial); }}
                sx={{
                    "& .MuiDialog-paper": {
                        borderRadius: 6,
                        backgroundImage: "none"
                    }
                }}
            >
                <DialogContent>
                    <Grid container sx={{ pt: 2, pb: 1, display: "flex", justifyContent: "center" }}>
                        <Grid item sx={{ p: 1.5, pr: .5, border: 1.5, borderRight: 0, borderColor: "divider", borderTopLeftRadius: 18, borderBottomLeftRadius: 18 }}>
                            <List
                                onScroll={handleHourScroll}
                                sx={{
                                    p: 0,
                                    overflow: "auto",
                                    maxHeight: 144,
                                    overscrollBehavior: "none",
                                    "&::-webkit-scrollbar": { width: 0 }
                                }}
                            >
                                {twentyfourhours.map((h, i) => (
                                    <ListItem key={`hour-${i}`} ref={(node) => (hourRef.current[i] = node)} sx={{ p: 0, height: 36, borderRadius: 6, bgcolor: value.hours() === h ? "primary.main" : "" }}>
                                        <ListItemButton onClick={() => { setHour(h) }} autoFocus={i === hourIndex} disableRipple sx={{ py: .5, px: 4, borderRadius: 6 }}>
                                            <ListItemText>
                                                <Typography color={value.hours() === h ? "primary.contrastText" : "text.primary"} fontSize={"0.75rem"} fontWeight="bold">{h.toString().padStart(2, '0')}</Typography>
                                            </ListItemText>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                        <Grid item sx={{ p: 1.5, pl: 0.5, border: 1.5, borderLeft: 0, borderColor: "divider", borderTopRightRadius: 18, borderBottomRightRadius: 18 }}>
                            <List
                                onScroll={handleMinuteScroll}
                                sx={{
                                    p: 0,
                                    ml: .5,
                                    overflow: "auto",
                                    maxHeight: 144,
                                    overscrollBehavior: "none",
                                    "&::-webkit-scrollbar": { width: 0 }
                                }}
                            >
                                {minutes.map((m, i) => (
                                    <ListItem key={`minute-${i}`} ref={(node) => (minuteRef.current[i] = node)} sx={{ p: 0, height: 36, borderRadius: 6, bgcolor: value.minutes() === m ? "primary.main" : "" }}>
                                        <ListItemButton onClick={() => { setMinute(m) }} autoFocus={i === minuteIndex} disableRipple sx={{ py: .5, px: 4, borderRadius: 6 }}>
                                            <ListItemText>
                                                <Typography color={value.minutes() === m ? "primary.contrastText" : "text.primary"} fontSize={"0.75rem"} fontWeight="bold">{m.toString().padStart(2, '0')}</Typography>
                                            </ListItemText>
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => { setIsOpen(false); setValue(initial); }}
                        sx={{
                            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
                            textTransform: "none",
                            border: theme.palette.mode === "dark" ? 1 : 0,
                            borderColor: "primary.main",
                            borderRadius: 6,
                            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main" },
                        }}
                    >{t("cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={() => { setTime(value); setIsOpen(false); }}
                        sx={{
                            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
                            textTransform: "none",
                            border: theme.palette.mode === "dark" ? 1 : 0,
                            borderColor: "primary.main",
                            borderRadius: 6,
                            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main" },
                        }}
                    >{t("accept")}</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
};

export default MobileWheelTimePicker;